// Element data for Atomic Mode. The material database YAML carries an
// `elements:` block (symbol, name, z, atomic_weight, ...). The runtime
// palette loader (`parseChemDoc`) only keeps materials, so this small helper
// pulls the periodic-table rows we need to turn materials into LAMMPS atoms:
// atomic number (z) and atomic weight (mass).

import { parseYaml } from "./yaml";

export interface ElementInfo {
  symbol: string;
  /** Atomic number. */
  z: number;
  /** Standard atomic weight (amu / g·mol⁻¹). */
  atomicWeight: number;
}

type Raw = Record<string, unknown>;

/**
 * Parse the `elements:` list out of a bundled material-db YAML string into a
 * `symbol -> ElementInfo` map. Rows missing a symbol, z, or atomic_weight are
 * skipped rather than guessed.
 */
export function parseElements(src: string): Map<string, ElementInfo> {
  const doc = parseYaml(src) as Raw;
  const rows = Array.isArray(doc.elements) ? (doc.elements as Raw[]) : [];
  const out = new Map<string, ElementInfo>();
  for (const row of rows) {
    const symbol = typeof row.symbol === "string" ? row.symbol : undefined;
    const z = typeof row.z === "number" ? row.z : undefined;
    const atomicWeight =
      typeof row.atomic_weight === "number" ? row.atomic_weight : undefined;
    if (!symbol || z === undefined || atomicWeight === undefined) continue;
    out.set(symbol, { symbol, z, atomicWeight });
  }
  return out;
}
